import { ActionHandler } from 'action-handler';
import { App } from './App';
import { FilterSettings } from './Filter/FilterSettings';
import { Candidate } from './Model/Candidate';
import { Department } from './Model/Department';
import { JobCenterModelInterface, ReferenceId } from './Model/JobCenterModelInterface';
import { JobVacancy } from './Model/JobVacancy';
import { ReferenceIdContainerInterface } from './ReferenceIdContainer';
import { BrowserStorageKeyHiddenDepartments, ReferenceIdBrowserStorage } from './Service/ReferenceIdBrowserStorage';
import { ServiceLocator } from './ServiceLocator';
import { Type } from './Type';

export interface AppState<M extends JobCenterModelInterface> {
    filterSettings: FilterSettings,
    candidates: Candidate[] | undefined
    jobVacancies: JobVacancy[] | undefined
    selectedJobVacancies: ReferenceIdContainerInterface
    hiddenDepartmentDescriptions: ReferenceIdContainerInterface
}

export class MainHandler<M extends Candidate | JobVacancy | JobCenterModelInterface> extends ActionHandler<App<M>, AppState<M>> {
    // @ts-ignore
    private readonly _serviceLocator: ServiceLocator;

    constructor(serviceLocator: ServiceLocator) {
        super();
        this._serviceLocator = serviceLocator;
    }

    public getInitialState(): AppState<M> {
        return {
            filterSettings: FilterSettings.default(),
            candidates: undefined,
            jobVacancies: undefined,
            selectedJobVacancies: this.loadSelectedJobVacancies(),
            hiddenDepartmentDescriptions: this.getReferenceIdBrowserStorage().load(BrowserStorageKeyHiddenDepartments),
        };
    }

    private loadSelectedJobVacancies() {
        return this.getReferenceIdBrowserStorage().load(Type.JobVacancy);
    }

    public isSelectedJobVacancy(job: JobVacancy): boolean {
        return this.state.selectedJobVacancies.contains(job.referenceId);
    }

    public selectJobVacancy(refId: ReferenceId): void;
    public selectJobVacancy(job: JobVacancy): void;
    public selectJobVacancy(jobOrRefId: JobVacancy | ReferenceId): void {
        if (typeof jobOrRefId !== 'string') {
            return this.selectJobVacancy(jobOrRefId.referenceId);
        }
        this.setState(ps => {
            console.debug(`[MainHandler] Add job vacancy ${jobOrRefId} to the selection`);
            const updatedContainer = ps.selectedJobVacancies.withAdded(jobOrRefId);
            this.getReferenceIdBrowserStorage().store(Type.JobVacancy, updatedContainer);

            return {selectedJobVacancies: updatedContainer};
        });
    }

    public unselectJobVacancy(refId: ReferenceId): void;
    public unselectJobVacancy(job: JobVacancy): void;
    public unselectJobVacancy(jobOrRefId: JobVacancy | ReferenceId): void {
        if (typeof jobOrRefId !== 'string') {
            return this.unselectJobVacancy(jobOrRefId.referenceId);
        }
        this.setState(ps => {
            console.debug(`[MainHandler] Remove job vacancy ${jobOrRefId} from the selection`);
            const updatedContainer = ps.selectedJobVacancies.withRemoved(jobOrRefId);
            this.getReferenceIdBrowserStorage().store(Type.JobVacancy, updatedContainer);

            return {selectedJobVacancies: updatedContainer};
        });
    }

    // noinspection JSUnusedGlobalSymbols
    public clearJobVacancySelection() {
        this.getReferenceIdBrowserStorage().clear(Type.JobVacancy);
    }

    public isDepartmentDescriptionHidden(department: Department): boolean {
        return this.state.hiddenDepartmentDescriptions.contains(department.referenceId);
    }

    public hideDepartmentDescription(department: Department): void {
        this.setState(ps => {
            console.debug(`[MainHandler] Hide description of department ${department.referenceId}`);
            const updatedContainer = ps.hiddenDepartmentDescriptions.withAdded(department.referenceId);
            this.getReferenceIdBrowserStorage().store(BrowserStorageKeyHiddenDepartments, updatedContainer);

            return {hiddenDepartmentDescriptions: updatedContainer};
        });
    }

    private getReferenceIdBrowserStorage(): ReferenceIdBrowserStorage {
        return this._serviceLocator.get('referenceIdBrowserStorage');
    }
}
